/** Recent searches (city / ZIP / free-text), kept client-side in localStorage.
 *  Distinct from recentlyViewed (listings) — this records what a buyer SEARCHED
 *  so a focused, empty search box can offer to resume it. */

export interface RecentSearch {
  /** Human label shown in the row, e.g. "Oakland", "94103", "willow st". */
  label: string;
  /** Ready-to-navigate href, e.g. "/homes?cities=oakland". */
  href: string;
  kind: 'city' | 'zip' | 'query';
  at: number;
  /**
   * The band the buyer had applied when they searched this place, if any.
   *
   * The href deliberately keeps only the *place* (so re-running the search is a
   * clean slate and the dedupe key stays stable), but the band is the strongest
   * signal a visitor with no saved search gives us — the home feed's "picked for
   * you" rail is built from it. Optional by construction: entries written before
   * W14 R136, and searches with no filters applied, simply don't have it.
   */
  priceMax?: number;
  bedsMin?: number;
}

const KEY = 'goreal:recent-searches';
const MAX = 6;

export function getRecentSearches(): RecentSearch[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentSearch[];
    return Array.isArray(parsed)
      ? parsed.filter((i) => i && typeof i.label === 'string' && typeof i.href === 'string')
      : [];
  } catch {
    return [];
  }
}

export function recordSearch(item: Omit<RecentSearch, 'at'>): void {
  if (typeof window === 'undefined' || !item.label || !item.href) return;
  try {
    // Dedupe by href so re-running the same search bubbles it to the top.
    const list = getRecentSearches().filter((i) => i.href !== item.href);
    // A band is only stored when it's a real number: writing `undefined` over a
    // previously recorded band would quietly forget it on the next bare search.
    const band: Pick<RecentSearch, 'priceMax' | 'bedsMin'> = {};
    if (typeof item.priceMax === 'number' && item.priceMax > 0) band.priceMax = item.priceMax;
    if (typeof item.bedsMin === 'number' && item.bedsMin > 0) band.bedsMin = item.bedsMin;
    list.unshift({ ...item, ...band, at: Date.now() });
    window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    // storage unavailable — feature silently off
  }
}

export function clearRecentSearches(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
